// src/services/liveWorkOrderService.js
// FlowSense MOW+ — work orders from the Live Readings page (wells, flood alerts, sensors) — NEW FILE
// Thin client over the existing work order API (backend routes/workOrders.js):
//   POST /workorders            create (roles: admin, staff, manager, supervisor, helpdesk — WO_CREATE_ROLES)
//   POST /workorders/ai/draft   duplicates + crew checklist + suggested due date (roles: staff, admin)
//   GET  /workorders?sensor_id= open jobs on a sensor
import api from './api';

// --- UPDATE (v1.2): server.js mounts the router at /api/v1/workorders (the Swagger comments in
// routes/workOrders.js say /work-orders, which 404s). Same base HelpdeskPage uses: api.post('/workorders').
export const WORK_ORDERS_API = '/workorders';
// --- END UPDATE

export const CLOSED_STATUSES = Object.freeze(['completed', 'closed', 'cancelled', 'canceled', 'rejected', 'resolved']);
export const isOpenWorkOrder = (wo) => !!wo && !CLOSED_STATUSES.includes(String(wo.status || '').toLowerCase());

// Fields POST /workorders accepts (anything else in a draft is display-only)
const CREATE_FIELDS = [
  'title', 'description', 'priority', 'status', 'due_date', 'category', 'source', 'asset_id', 'assigned_user_id',
  'work_order_type', 'domain', 'road_id', 'sensor_id', 'well_id', 'parent_work_order_id', 'inspection_ref_id',
  'inspection_source', 'latitude', 'longitude', 'location_description', 'prediction_context',
];

/** Draft → request body: known fields only, no empty values (the validator rejects '' for ids). */
export const toCreatePayload = (draft = {}) => {
  const out = {};
  CREATE_FIELDS.forEach((k) => {
    let v = draft[k];
    if (v === undefined || v === null) return;
    if (typeof v === 'string') { v = v.trim(); if (!v) return; }
    if ((k === 'latitude' || k === 'longitude') && !Number.isFinite(Number(v))) return;
    out[k] = k === 'latitude' || k === 'longitude' ? Number(v) : v;
  });
  if (out.due_date) out.due_date = String(out.due_date).slice(0, 10);
  return out;
};

/** Readable message for a failed request (validation list, role, network). */
export const describeWorkOrderError = (e) => {
  const status = e?.response?.status;
  const body = e?.response?.data || {};
  if (status === 403) return 'Your role cannot create work orders. Ask a supervisor, staff member or admin.';
  if (Array.isArray(body.errors) && body.errors.length) return body.errors.map((x) => x.msg || x.message).filter(Boolean).join(' ');
  if (body.message) return body.message;
  if (!e?.response) return 'Could not reach FlowSense. Check your connection and try again.';
  return e?.message || 'The work order could not be created.';
};

export const createWorkOrder = async (draft) => {
  const res = await api.post(WORK_ORDERS_API, toCreatePayload(draft));
  const wo = res?.data?.workOrder || res?.data?.data || null;
  if (!wo?.id) throw new Error(res?.data?.message || 'The server did not return the new work order.');
  return wo;
};

/** Duplicates / checklist / suggested due date. Resolves to null when the helper is unavailable (role, offline). */
export const fetchWorkOrderHelp = async (draft, { signal } = {}) => {
  try {
    const body = toCreatePayload(draft);
    const res = await api.post(`${WORK_ORDERS_API}/ai/draft`, {
      title: body.title, description: body.description, priority: body.priority, asset_id: body.asset_id,
      domain: body.domain, work_order_type: body.work_order_type, category: body.category,
      sensor_id: body.sensor_id, well_id: body.well_id, road_id: body.road_id,
    }, { signal });
    return res?.data?.success === false ? null : res?.data || null;
  } catch (_) {
    return null;
  }
};

/** Open work orders raised against one sensor (the sensor drawer only listed the linked asset's jobs). */
export const fetchOpenWorkOrdersForSensor = async (deviceId, { signal } = {}) => {
  if (!deviceId) return [];
  const res = await api.get(WORK_ORDERS_API, { params: { sensor_id: String(deviceId), limit: 50, sort_by: 'created_at', sort_dir: 'desc' }, signal });
  const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
  return list.filter(isOpenWorkOrder);
};
